class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    // If window.location.hostname is localhost, set this.recommendationsUrl http://localhost:9002/recommendations/products.json
    // If window.location.hostname is tundra.ngrok.io, set this.recommendationsUrl https://sharks.ngrok.io/recommendations/products.json
    // Otherwise, set this.recommendationsUrl https://yippyapp.myshopify.com/recommendations/products.json
    if (window.location.hostname === "localhost") {
      this.recommendationsUrl = "http://localhost:9002/recommendations/products.json";
    } else if (window.location.hostname === "tundra.ngrok.io") {
      this.recommendationsUrl = "https://sharks.ngrok.io/recommendations/products.json";
    } else {
      this.recommendationsUrl = "https://yippyapp.co/recommendations/products.json";
      // this.recommendationsUrl = "https://yippyapp.myshopify.com/recommendations/products.json";
    }

    this.limit = this.dataset.limit || 8;

    // Slider state
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;

    this.loadRecommendations();
    this.setupEventListeners();

    // Listen for cart changes
    document.addEventListener("cart:refresh", () => {
      this.loadRecommendations();
    });
  }

  setupEventListeners() {
    // Re-initialize slider after recommendations load
    this.addEventListener('recommendations:loaded', () => {
      this.initSlider();
    });
  }

  initSlider() {
    const slider = this.querySelector('.cart-recommendations__list');
    if (!slider) return;

    let startX;
    let scrollLeft;
    let isMouseDown = false;

    const handleDragStart = (e) => {
      // Store initial touch position for determining if it's a drag or click
      startX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      scrollLeft = slider.scrollLeft;

      if (e.type.includes('mouse')) {
        isMouseDown = true;
        slider.classList.add('is-dragging');
        e.preventDefault();

        // Disable image dragging
        const images = slider.getElementsByTagName('img');
        Array.from(images).forEach(img => {
          img.draggable = false;
        });

        // Add document-level mousemove listener
        document.addEventListener('mousemove', handleDragMove);
      }
    };

    const handleDragEnd = (e) => {
      if (e.type.includes('mouse') && !isMouseDown) return;

      if (e.type.includes('mouse')) {
        isMouseDown = false;
        slider.classList.remove('is-dragging');
        document.removeEventListener('mousemove', handleDragMove);
      }

      // Re-enable click events immediately for touch, after delay for mouse
      const delay = e.type.includes('mouse') ? 50 : 0;
      setTimeout(() => {
        const links = slider.getElementsByTagName('a');
        Array.from(links).forEach(link => {
          link.style.pointerEvents = 'auto';
        });
      }, delay);
    };

    const handleMouseLeave = () => {
      if (!isMouseDown) return;
      slider.classList.remove('is-dragging');
    };

    const handleMouseEnter = (e) => {
      if (!isMouseDown) return;
      startX = e.pageX;
      scrollLeft = slider.scrollLeft;
      slider.classList.add('is-dragging');
    };

    const handleDragMove = (e) => {
      if (e.type.includes('mouse') && !isMouseDown) return;

      const x = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      const delta = x - startX;
      const moveDistance = Math.abs(delta);

      // Only treat as drag if moved more than 5px
      if (moveDistance > 5) {
        if (e.type.includes('mouse')) {
          e.preventDefault();
          e.stopPropagation();
        }

        slider.scrollLeft = scrollLeft - delta;

        // Only disable pointer events for mouse interactions
        if (e.type.includes('mouse')) {
          const links = slider.getElementsByTagName('a');
          Array.from(links).forEach(link => {
            link.style.pointerEvents = 'none';
          });
        }
      }
    };

    // Mouse Events
    slider.addEventListener('mousedown', handleDragStart);
    slider.addEventListener('mouseup', handleDragEnd);
    slider.addEventListener('mouseleave', handleMouseLeave);
    slider.addEventListener('mouseenter', handleMouseEnter);

    // Touch Events
    slider.addEventListener('touchstart', handleDragStart);
    slider.addEventListener('touchmove', handleDragMove);
    slider.addEventListener('touchend', handleDragEnd);
    slider.addEventListener('touchcancel', handleDragEnd);

    // Prevent default dragging behavior
    slider.addEventListener('dragstart', (e) => e.preventDefault());

    // Global mouse up handler
    document.addEventListener('mouseup', handleDragEnd);
  }

  formatMoney(cents) {
    const format = Shopify.money_format || "${{amount}}";
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const value = parseInt(cents || 0, 10);
    const dollars = Math.floor(value / 100);
    const cents_part = (value % 100).toString().padStart(2, "0");
    return format
      .replace("{{amount}}", `${dollars}.${cents_part}`)
      .replace("{{amount_no_decimals}}", dollars)
      .replace("{{amount_with_comma_separator}}", `${dollars},${cents_part}`);
  }

  loadRecommendations() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    // Show loading state
    this.innerHTML = `
      <div class="cart-recommendations__loading" style="text-align: center; padding: 20px;">
        <div class="loading-spinner" style="display: inline-block; width: 30px; height: 30px; border: 3px solid #f3f3f3; border-top: 3px solid var(--color-primary-300); border-radius: 50%; animation: spin 1s linear infinite;"></div>
      </div>
    `;

    // Get the first product ID from the cart
    const cartItems = document.querySelectorAll(".cart-item");

    if (cartItems.length === 0) {
      this.handleNoRecommendations();
      return;
    }

    const firstItem = cartItems[0];
    const productId = firstItem.dataset.productId;

    if (!productId) {
      this.handleNoRecommendations();
      return;
    }

    // Fetch recommendations
    fetch(`${this.recommendationsUrl}?product_id=${productId}&limit=${this.limit}`)
      .then(response => response.json())
      .then(data => {
        if (!data.products || data.products.length === 0) {
          this.handleNoRecommendations();
          return;
        }

        // Create recommendations HTML
        this.innerHTML = `
          <h3 class="cart-recommendations__heading">
            People Also Bought
          </h3>
          <ul class="cart-recommendations__list">
            ${data.products.map(product => `
              <li class="cart-recommendations__item">
                <a href="${product.url}" class="cart-recommendations__link">
                  <img
                    src="${product.featured_image}"
                    alt="${product.title}"
                    loading="lazy"
                  >
                  <div class="cart-recommendations__info">
                    <h4>${product.title}</h4>
                  </div>
                </a>
              </li>
            `).join("")}
          </ul>
        `;
      })
      .catch(error => {
        console.error("Error loading recommendations:", error);
        this.handleNoRecommendations();
      })
      .finally(() => {
        this.isLoading = false;
        this.dispatchEvent(new CustomEvent('recommendations:loaded'));
      });
  }

  handleNoRecommendations() {
    this.isLoading = false;
    this.innerHTML = `
      <div class="cart-recommendations__empty" style="text-align: center; padding: 20px; color: #666;">
        <p style="margin: 0; font-family: 'DMSans'; font-size: 14px;">No recommendations available at this time.</p>
      </div>
    `;
    this.dispatchEvent(new CustomEvent("recommendations:empty"));
  }
}

if (!customElements.get("cart-recommendations")) {
  customElements.define("cart-recommendations", CartRecommendations);
}
